import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './HubSpotContactForm.css';

const HubSpotContactForm: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    company: '',
    firstName: '',
    lastName: '',
    email: '',
    city: '',
    state: '',
    inquiryType: 'general', // general, press, feedback
    message: '',
    requestDemo: false,
    subscribeNewsletter: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // HubSpot Partner ID
  const partnerId = '48197990';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Log form data for tracking purposes
    console.log('Form data for HubSpot tracking:', formData);
    
    // Send tracking event to HubSpot
    if ((window as any)._hsq) {
      (window as any)._hsq.push(['identify', {
        email: formData.email,
        firstname: formData.firstName,
        lastname: formData.lastName,
        company: formData.company,
        city: formData.city,
        state: formData.state,
        inquiry_type: formData.inquiryType,
        request_demo: formData.requestDemo ? 'Yes' : 'No',
        newsletter_subscription: formData.subscribeNewsletter ? 'Yes' : 'No'
      }]);
      
      (window as any)._hsq.push(['trackEvent', {
        id: 'contact_form_submission',
        value: partnerId
      }]);
    }
    
    // Simulate form submission delay
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form data
      setFormData({
        company: '',
        firstName: '',
        lastName: '',
        email: '',
        city: '',
        state: '',
        inquiryType: 'general',
        message: '',
        requestDemo: false,
        subscribeNewsletter: false
      });
    }, 800);
  };

  // Get form class based on inquiry type
  const getFormClass = () => {
    const baseClass = 'hubspot-contact-form gesslar-contact-form';
    switch(formData.inquiryType) {
      case 'press': return `${baseClass} press-inquiry-contact-form press-contact-form contact-form-press`;
      case 'feedback': return `${baseClass} feedback-contact-form platform-feedback-form contact-form-feedback`;
      case 'general': 
      default: return `${baseClass} general-inquiry-contact-form general-question-form contact-form-general`;
    }
  };

  return (
    <div className={getFormClass()}>
      {isSubmitted ? (
        <div className="success-message">
          <h3>{t('signup.success.title')}</h3>
          <p>{t('signup.success.message')}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="inquiryType">{t('forms.labels.inquiryType')}</label>
            <select
              id="inquiryType"
              name="inquiryType"
              value={formData.inquiryType}
              onChange={handleInputChange}
              required
            >
              <option value="general">{t('forms.options.inquiryTypes.general')}</option>
              <option value="press">{t('forms.options.inquiryTypes.press')}</option>
              <option value="feedback">{t('forms.options.inquiryTypes.feedback')}</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="company">{t('forms.labels.companyName')}</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              required
              placeholder={t('forms.placeholders.company')}
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('forms.labels.firstName')} *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                placeholder={t('forms.placeholders.firstName')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">{t('forms.labels.lastName')} *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                placeholder={t('forms.placeholders.lastName')}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('forms.labels.email')} *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder={t('forms.placeholders.email')}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">{t('forms.labels.city')}</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder={t('forms.placeholders.city')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="state">{t('forms.labels.state')}</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder={t('forms.placeholders.state')}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="message">{t('forms.labels.message')} *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              placeholder={t('forms.placeholders.message')}
              rows={5}
            />
          </div>

          <div className="form-checkbox">
            <input
              type="checkbox"
              id="requestDemo"
              name="requestDemo"
              checked={formData.requestDemo}
              onChange={handleCheckboxChange}
            />
            <label htmlFor="requestDemo">{t('forms.labels.requestDemo')}</label>
          </div>

          <div className="form-checkbox">
            <input
              type="checkbox"
              id="subscribeNewsletter"
              name="subscribeNewsletter"
              checked={formData.subscribeNewsletter}
              onChange={handleCheckboxChange}
            />
            <label htmlFor="subscribeNewsletter">{t('forms.labels.subscribeNewsletter')}</label>
          </div>
          
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('contact.loading') : t('contact.form.submit')}
          </button>
        </form>
      )}
    </div>
  );
};

export default HubSpotContactForm; 